/**
 * Single source of truth for per-root opp_type visuals + labels.
 *
 * Every component that renders a root opp_type icon / chip / tab —
 * the right-side tab strip, the message-pane opp dropdown, the
 * left lead-list row icons, the opp-card corner chip — imports
 * from here. To add a new root (e.g. Parts), append one entry to
 * ROOT_OPP_TYPES below; nothing else in the FE needs to change.
 *
 * The id MUST match the BE's ``opportunity_type.id`` of the root
 * row. ``isUserUnscoped`` / ``getUserAllowedRootOppTypeIds`` in
 * ``oppTypeScoping.js`` derive permission against these same ids.
 *
 * Dynamic-discovery note: the tab strip in ``OppTypeRootTabs``
 * derives its tab list from ``Location.activeRootOppTypeIds``
 * (BE field), intersected with the lead's opp roots and the user's
 * permitted roots. When a root id surfaces from the BE that doesn't
 * have an entry below (e.g. a tenant-specific root like "AutoSavvy
 * Customer Service"), ``getRootConfig`` returns a generic fallback
 * so the tab still renders — no FE deploy required to enable a new
 * root at a customer, only to give it its branded icon/colors.
 */
import React from "react";
import { LuBriefcaseBusiness, LuPackage, LuTag, LuWrench } from "react-icons/lu";

// Custom AutoSavvy Customer Service icon. Hand-traced from the
// designer's ``customer-service-line 1.svg`` so the tab matches the
// rest of the design system exactly (a filled-style headset distinct
// from Lucide's stroke-only ``LuHeadphones``). Exposes the same prop
// shape as Lucide icons — ``size`` + ``color`` + ``strokeWidth`` —
// so consumers can swap it in without special-casing. ``color`` maps
// to ``fill`` since the underlying glyph is a filled path.
const LuAcsHeadset = ({ size = 16, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14.6666 11.3347C14.6659 13.2487 13.321 14.8483 11.5247 15.241L11.0994 13.965C11.9021 13.834 12.5843 13.3447 12.9765 12.6666H11.3333C10.5969 12.6666 9.99992 12.0697 9.99992 11.3333V8.66659C9.99992 7.93019 10.5969 7.33325 11.3333 7.33325H13.292C12.9639 4.70238 10.7197 2.66659 7.99992 2.66659C5.28017 2.66659 3.03591 4.70238 2.70785 7.33325H4.66658C5.40297 7.33325 5.99992 7.93019 5.99992 8.66659V11.3333C5.99992 12.0697 5.40297 12.6666 4.66658 12.6666H2.66659C1.93021 12.6666 1.33325 12.0697 1.33325 11.3333V7.99992C1.33325 4.31802 4.31802 1.33325 7.99992 1.33325C11.6818 1.33325 14.6666 4.31802 14.6666 7.99992V8.66572V8.66659V11.3333V11.3347ZM13.3333 11.3333V8.66659H11.3333V11.3333H13.3333ZM2.66659 8.66659V11.3333H4.66658V8.66659H2.66659Z"
      fill={color}
    />
  </svg>
);

// ``DEFAULT_ROOT_OPP_TYPES`` — the fallback set used when the BE
// returns no ``activeRootOppTypeIds`` for the active location (no
// Department configured, or every Department row at the location is
// missing an ``opportunity_type_id`` mapping). Industry-universal
// roots only: Sales (1), Service (2), Parts (3). Tenant-specific
// roots (e.g. AutoSavvy CS) MUST be backed by a real Department
// mapping at the BE — they never surface via the default fallback.
//
// Ordered list — display order in tab strip / row icons / nested
// dropdown follows this order. Sort by ``rootOppTypeId`` ascending
// when adding new entries to keep the strip stable.
export const DEFAULT_ROOT_OPP_TYPES = [
  {
    rootOppTypeId: 1,
    key: "SALES",
    displayName: "Sales",
    // ``shortLabel`` is the compact tab/dropdown text. When equal to
    // ``displayName`` (Sales/Service), components don't bother with a
    // tooltip. For long names (e.g. "AutoSavvy Customer Service" →
    // "ACS"), components render shortLabel with displayName as the
    // hover title.
    shortLabel: "Sales",
    rootName: "sales",
    allLabel: "All sales",
    Icon: LuBriefcaseBusiness,
    // ``stroke`` is the icon color on bare icons + chips.
    // ``chipBg`` is the tint behind the icon when the root is the
    // active / highlighted one in a row of multiple icons.
    // ``activeColor`` is the tab ink-bar + active label color.
    stroke: "#0958D9",
    chipBg: "#E6F1FB",
    activeColor: "#1677FF",
  },
  {
    rootOppTypeId: 2,
    key: "SERVICE",
    displayName: "Service",
    shortLabel: "Service",
    rootName: "service",
    allLabel: "All service",
    Icon: LuWrench,
    stroke: "#D46B08",
    chipBg: "#FFF1D6",
    activeColor: "#D46B08",
  },
  {
    rootOppTypeId: 3,
    key: "PARTS",
    displayName: "Parts",
    shortLabel: "Parts",
    rootName: "parts",
    allLabel: "All parts",
    // Lucide LuPackage matches the design spec (Colors-Base-Purple-6
    // #722ED1) — same hex-prism glyph the designer's Parts.svg uses,
    // just consumed as an icon component for consistency with the
    // other entries.
    Icon: LuPackage,
    stroke: "#722ED1",
    chipBg: "#F3E5F5",
    activeColor: "#722ED1",
  },
];

// ``TENANT_ROOT_OPP_TYPES`` — branded configs for roots that exist at
// specific customers but are NOT part of the industry-default
// fallback. Surfaces only when the BE returns the matching id via
// ``Location.activeRootOppTypeIds`` (i.e. the customer actually has a
// Department row mapping a Department to this opp_type). Never shown
// when the BE returns ``[]``.
export const TENANT_ROOT_OPP_TYPES = [
  {
    // Tenant-specific root for AutoSavvy. BE id confirmed via the
    // ``department`` table (opportunity_type_id=199 on locations like
    // 358). Cyan branding per design (var(--Colors-Base-Cyan-6) =
    // #13C2C2) using the bespoke ``customer-service-line`` glyph.
    rootOppTypeId: 199,
    key: "AUTOSAVVY_CS",
    displayName: "AutoSavvy Customer Service",
    // Long display name — abbreviated for the tab strip / message
    // dropdown; tooltip surfaces the full text.
    shortLabel: "AutoSavvy CS",
    rootName: "autosavvy customer service",
    allLabel: "All AutoSavvy CS",
    Icon: LuAcsHeadset,
    stroke: "#13C2C2",
    chipBg: "#E6FFFB",
    activeColor: "#13C2C2",
  },
];

// Union of default + tenant configs. ``getRootConfig`` looks up
// against this so a BE-supplied tenant root id still resolves to its
// branded config (icon / color / labels). Only ``DEFAULT_ROOT_OPP_TYPES``
// is used as the fallback when the BE returns no roots.
export const ROOT_OPP_TYPES = [...DEFAULT_ROOT_OPP_TYPES, ...TENANT_ROOT_OPP_TYPES];

// Neutral fallback used for roots the BE exposes but which don't
// have a branded entry above (e.g. tenant-specific roots like
// "AutoSavvy Customer Service" enabled at one customer). The tab
// still renders so the customer isn't blocked on a FE deploy — it
// just gets a generic icon + neutral color until a real entry is
// added. ``displayName``/``shortLabel``/``rootName``/``allLabel`` are
// filled in by :func:`getRootConfig` from the BE-supplied name.
const FALLBACK_ROOT_CONFIG = {
  rootOppTypeId: null,
  key: "GENERIC",
  displayName: "Other",
  shortLabel: "Other",
  rootName: "other",
  allLabel: "All",
  Icon: LuTag,
  stroke: "rgba(0, 0, 0, 0.65)",
  chipBg: "rgba(0, 0, 0, 0.06)",
  activeColor: "rgba(0, 0, 0, 0.85)",
};

// Compact label for long opp-type names. Returns the input unchanged
// when it's short enough to fit a tab without truncation; otherwise
// keeps the first word in full and abbreviates the rest to their
// initials. Examples:
//   "AutoSavvy Customer Service" → "AutoSavvy CS"
//   "Heavy Equipment Sales"      → "Heavy ES"
//   "Sales"                      → "Sales"   (under the threshold)
//
// Threshold (12 chars) is roughly where the tab strip starts
// wrapping awkwardly on common viewport widths; tune if needed.
const SHORT_LABEL_THRESHOLD = 12;
const abbreviateLongName = name => {
  if (!name) return "";
  const trimmed = String(name).trim();
  if (trimmed.length <= SHORT_LABEL_THRESHOLD) return trimmed;
  const words = trimmed.split(/\s+/).filter(Boolean);
  // Single-word long name (no spaces) — initials would be one letter,
  // worse than truncating. Fall back to "first-N chars + ellipsis".
  if (words.length < 2) return `${trimmed.slice(0, SHORT_LABEL_THRESHOLD - 1)}…`;
  // First word as-is + initials of the remaining words, upper-cased.
  const tail = words
    .slice(1)
    .map(w => w[0])
    .join("")
    .toUpperCase();
  return `${words[0]} ${tail}`;
};

// Lookup with safe fallback. Use this anywhere a component needs
// visuals for a specific root id — never reach into ROOT_OPP_TYPES
// with a hard-coded index. When the id is unknown, returns a copy of
// ``FALLBACK_ROOT_CONFIG`` stamped with the actual id and (when
// supplied) the BE-supplied display name + auto-abbreviated short
// label.
export const getRootConfig = (rootOppTypeId, opportunityTypeName = null) => {
  const found = ROOT_OPP_TYPES.find(c => Number(c.rootOppTypeId) === Number(rootOppTypeId));
  if (found) return found;
  // Use the BE name verbatim for the long label; auto-abbreviate for
  // the short one. Falls back to "Other" when nothing was supplied.
  const display = opportunityTypeName
    ? String(opportunityTypeName)
    : FALLBACK_ROOT_CONFIG.displayName;
  const lower = display.toLowerCase();
  return {
    ...FALLBACK_ROOT_CONFIG,
    rootOppTypeId: rootOppTypeId != null ? Number(rootOppTypeId) : null,
    displayName: display,
    shortLabel: abbreviateLongName(display),
    rootName: lower,
    allLabel: `All ${lower}`,
  };
};

// Set of configured root ids — handy for "is this id one we know about"
// checks without scanning the array repeatedly. Unknown ids still
// render via the fallback in ``getRootConfig`` but are excluded from
// this set.
export const KNOWN_ROOT_IDS = new Set(ROOT_OPP_TYPES.map(c => Number(c.rootOppTypeId)));
